import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import LabelService from "../../../Services/label/LabelService";
import BudgetTrackService from "../../../Services/budgetTrack/BudgetTrackService";
import {
  BudgetTrackStats,
  BudgetTrackInbox,
  BudgetTrackDetailPane,
  BudgetTrackSettingsDrawer,
} from "./BudgetTrackInboxLayout";
import { budgetTrackQueryOptions } from "./budgetTrackQueryOptions";
import { formatDateTime } from "./budgetTrackUtils";
import "./budget-track.scss";

function useQueryParams() {
  return useMemo(() => new URLSearchParams(window.location.search), []);
}

export default function BudgetTrackContainer() {
  const queryClient = useQueryClient();
  const params = useQueryParams();
  const [labelId, setLabelId] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [reviewFilter, setReviewFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [creatingExpenseId, setCreatingExpenseId] = useState(null);

  const labelsQuery = useQuery({ queryKey: ["labels"], queryFn: LabelService.getLabels });
  const labels = labelsQuery.data?.data || [];

  useEffect(() => {
    if (labels.length && !labelId) {
      const fromUrl = Number(params.get("label_id"));
      setLabelId(fromUrl && labels.some((l) => l.id === fromUrl) ? fromUrl : labels[0].id);
    }
  }, [labels, labelId, params]);

  const oauthToastShown = React.useRef(false);
  useEffect(() => {
    if (oauthToastShown.current) return;
    if (params.get("connected") === "1") {
      toast.success("Mailbox connected");
      oauthToastShown.current = true;
    } else if (params.get("connected") === "0") {
      toast.error("Connection failed");
      oauthToastShown.current = true;
    }
  }, [params]);

  const enabled = Boolean(labelId);

  const connectionQuery = useQuery({
    queryKey: ["budgetTrackConnection", labelId],
    queryFn: () => BudgetTrackService.getConnection(labelId).then((r) => r.data),
    enabled,
    ...budgetTrackQueryOptions,
  });

  const overviewQuery = useQuery({
    queryKey: ["budgetTrackOverview", labelId, year],
    queryFn: () => BudgetTrackService.getOverview(labelId, year).then((r) => r.data),
    enabled,
    ...budgetTrackQueryOptions,
  });

  const vendorsQuery = useQuery({
    queryKey: ["budgetTrackVendors", labelId],
    queryFn: () => BudgetTrackService.getVendors(labelId).then((r) => r.data.vendors),
    enabled,
    ...budgetTrackQueryOptions,
  });

  const emailsQuery = useQuery({
    queryKey: ["budgetTrackEmails", labelId],
    queryFn: () => BudgetTrackService.getEmails(labelId, { per_page: 100 }).then((r) => r.data),
    enabled,
    ...budgetTrackQueryOptions,
  });

  const historyQuery = useQuery({
    queryKey: ["budgetTrackHistory", labelId],
    queryFn: () => BudgetTrackService.getSyncHistory(labelId).then((r) => r.data.runs),
    enabled,
    ...budgetTrackQueryOptions,
  });

  const scanRulesQuery = useQuery({
    queryKey: ["budgetTrackScanRules", labelId],
    queryFn: () => BudgetTrackService.getScanRules(labelId).then((r) => r.data.rules),
    enabled,
    ...budgetTrackQueryOptions,
  });

  const connectMutation = useMutation({
    mutationFn: () => BudgetTrackService.getAuthUrl(labelId).then((r) => r.data.auth_url),
    onSuccess: (url) => { window.location.href = url; },
    onError: () => toast.error("OAuth failed"),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => BudgetTrackService.disconnect(labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetTrackConnection", labelId] });
      setSelectedEmail(null);
      toast.success("Disconnected");
    },
  });

  const syncMutation = useMutation({
    mutationFn: (forceFull) => BudgetTrackService.sync(labelId, forceFull),
    onSuccess: () => {
      toast.success("Sync started");
      queryClient.invalidateQueries({ queryKey: ["budgetTrackConnection", labelId] });
      queryClient.invalidateQueries({ queryKey: ["budgetTrackEmails", labelId] });
      queryClient.invalidateQueries({ queryKey: ["budgetTrackOverview", labelId] });
      queryClient.invalidateQueries({ queryKey: ["budgetTrackHistory", labelId] });
    },
  });

  const linkExpensesMutation = useMutation({
    mutationFn: () => BudgetTrackService.linkExpenses(labelId, true),
    onSuccess: (res) => {
      const s = res.data?.stats || {};
      toast.success(`Linked ${s.linked_existing || 0}, created ${s.created || 0}`);
      queryClient.invalidateQueries({ queryKey: ["budgetTrackEmails", labelId] });
    },
  });

  const saveEmailMutation = useMutation({
    mutationFn: ({ id, payload }) => BudgetTrackService.updateEmail(id, payload),
    onSuccess: (_, { payload }) => {
      if (selectedEmail) {
        setSelectedEmail({ ...selectedEmail, ...payload });
      }
      queryClient.invalidateQueries({ queryKey: ["budgetTrackEmails", labelId] });
      queryClient.invalidateQueries({ queryKey: ["budgetTrackOverview", labelId] });
      toast.success("Saved");
    },
  });

  const createExpenseMutation = useMutation({
    mutationFn: (email) => BudgetTrackService.createExpense(email.id),
    onMutate: (email) => setCreatingExpenseId(email.id),
    onSettled: () => setCreatingExpenseId(null),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["budgetTrackEmails", labelId] });
      if (selectedEmail && res.data?.expense_id) {
        setSelectedEmail({ ...selectedEmail, expense_id: res.data.expense_id });
      }
      toast.success("Expense created");
    },
  });

  const saveRulesMutation = useMutation({
    mutationFn: (rules) => BudgetTrackService.updateScanRules(labelId, rules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetTrackScanRules", labelId] });
      toast.success("Rules saved");
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => BudgetTrackService.exportEmails(labelId, {}),
    onSuccess: (response) => {
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `budget-track-${labelId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  const connection = connectionQuery.data?.connection;
  const connected = connectionQuery.data?.connected;
  const syncing = connection?.sync_status === "syncing" || syncMutation.isPending;

  return (
    <Container fluid className="btx">
      {/* Toolbar */}
      <div className="btx-toolbar">
        <span className="btx-toolbar__brand">BudgetTrack</span>

        <select
          className="form-select form-select-sm btx-toolbar__select"
          value={labelId || ""}
          onChange={(e) => {
            setLabelId(Number(e.target.value));
            setSelectedEmail(null);
          }}
        >
          {labels.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name || l.label_name || `Label ${l.id}`}
            </option>
          ))}
        </select>

        <span className="btx-toolbar__status">
          <span className={`btx-toolbar__status-dot ${!connected ? "off" : syncing ? "syncing" : ""}`} />
          {connected ? (
            <>
              {connection?.mailbox_email?.split("@")[0]}… ·{" "}
              {formatDateTime(connection?.last_synced_at)}
            </>
          ) : (
            "Not connected"
          )}
        </span>

        <div className="btx-toolbar__spacer" />

        <div className="btx-toolbar__btn-group">
          {!connected ? (
            <button type="button" className="btn btn-sm btn-primary" onClick={() => connectMutation.mutate()}>
              Connect Gmail
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={syncing}
                onClick={() => syncMutation.mutate(false)}
              >
                {syncing ? "Syncing…" : "Sync"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={linkExpensesMutation.isPending}
                onClick={() => linkExpensesMutation.mutate()}
              >
                Link
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={exportMutation.isPending}
                onClick={() => exportMutation.mutate()}
              >
                Export
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSettingsOpen(true)}>
                ⚙
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => disconnectMutation.mutate()}
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {connection?.needs_reauth && (
        <div className="btx-reauth">Gmail needs re-authentication. Connect again to resume syncing.</div>
      )}

      <BudgetTrackStats overview={overviewQuery.data} />

      <div className="btx-split">
        <BudgetTrackInbox
          emails={emailsQuery.data}
          loading={emailsQuery.isLoading}
          selectedId={selectedEmail?.id}
          onSelect={setSelectedEmail}
          reviewFilter={reviewFilter}
          onReviewFilterChange={setReviewFilter}
          search={search}
          onSearchChange={setSearch}
          creatingExpenseId={creatingExpenseId}
        />

        <BudgetTrackDetailPane
          email={selectedEmail}
          overview={overviewQuery.data}
          year={year}
          onYearChange={setYear}
          vendors={vendorsQuery.data}
          runs={historyQuery.data}
          syncing={syncing}
          currencies={overviewQuery.data?.currencies}
          saving={saveEmailMutation.isPending}
          creating={creatingExpenseId === selectedEmail?.id}
          onSave={(payload) => saveEmailMutation.mutate({ id: selectedEmail.id, payload })}
          onCreateExpense={(email) => createExpenseMutation.mutate(email)}
        />
      </div>

      <BudgetTrackSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        rules={scanRulesQuery.data}
        saving={saveRulesMutation.isPending}
        onSave={(rules) => saveRulesMutation.mutate(rules)}
        runs={historyQuery.data}
        syncing={syncing}
      />
    </Container>
  );
}
